import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import { fetchColorListLibrary } from '../../../../Library/ColorLibary/store/ColorLibrary.action';
import { getSampleTraking } from '../../../store/DevelopmentOrder.action';
import { getBulkFilteredPOs } from '../../../SubmitDownload/Store/Submit.action';
import { getCreatedByUser } from '../../../../Dashboard/TodoList/PurchaseOrder/store/Purchase.action';
import {
  getProductDetailsBatch,
  getCoreProductDetailsBatch,
  getBomDocumentsBatch,
  getMeasurementDocumentsBatch,
  getPoDocumentsBatch,
  getDesignDocumentsBatch,
  getSamplesBatch,
  getProductSamplesBatch,
  getSampleTypeGroupsBatch,
} from '../../../../../services/multiSubmitBatch.service';
import type { CoreProductDetailsDto } from '../../../../../services/multiSubmitBatch.service';
import { getCostingTemplateListByDevOrderService } from '../../../../../services/costing-template.service';
import type { GradingGroupOption, RawProductSample, RawSampleTypeGroup, RowOptions, SelectOption } from '../types';
import { EMPTY_ROW_OPTIONS } from '../types';
import { isAutomatedSampleTypeGroup, productHasSampleInGroup } from '../sampleChain';
import type { TrackingDate } from '../submitPayload';

type Dispatch = ThunkDispatch<unknown, unknown, AnyAction>;

interface RawDoc {
  _id: string;
  fileName?: string;
  documentName?: string;
  documentTitle?: string;
  createdAt?: string | null;
  docType?: string | null;
}
interface RawPO {
  _id: string;
  purchaseOrderNumber?: string;
  poOrderNo?: string;
  orderDetails?: Array<{ productNumber?: string }>;
}
interface RawSample {
  _id: string;
  supplier?: string;
  sampleNumber?: string;
  createdAt?: string | null;
  sampleTypeName?: string;
}
interface RawUser {
  email: string;
  firstName: string;
  lastName: string;
}
interface RawCostingTemplate {
  _id: string;
  name?: string;
}

const opt = (label: string, value: string): SelectOption => ({ label, value });
const dated = (name: string, createdAt?: string | null) => (createdAt ? `${name} ${moment(createdAt).format('DD-MMMM-YYYY, HH:mm')}` : name);

interface RawTracking {
  isActive?: boolean;
  name: string;
  refKey?: string;
  key?: string;
  value?: string;
  trackingUrl?: string;
  _id: string;
}

/** Map one sample-tracking-master entry into the TrackingDate carried in sampleRequest. */
function mapTrackingEntry(t: RawTracking): TrackingDate {
  return {
    label: `${t.name.trim()} Date`,
    value: t.refKey === 'requested' ? null : t.value || null,
    trackingUrl: t.trackingUrl || null,
    sampleId: t._id,
    key: t.refKey || t.key || '',
    _id: t._id,
  };
}

interface BuildRowOptionsArgs {
  productDetails: { gradingGroups?: GradingGroupOption[]; colourLibraries?: string[]; legLengths?: SelectOption[] } | boolean;
  bomRes: RawDoc[] | undefined;
  otherRes: { data?: RawDoc[] } | undefined;
  poDocRes: RawDoc[] | undefined;
  designRes: { data?: RawDoc[] } | undefined;
  sampleRes: { data?: RawSample[] } | undefined;
  supplierId: string;
  poOptions: SelectOption[];
  colourLib: Array<{ _id: string; name: string }>;
  /** Sample Type Group rows for this product, from the batch endpoint (drives the per-line dropdown). */
  sampleTypeGroupRaw?: RawSampleTypeGroup[];
  /** Full (un-supplier-filtered) sample list for this product, used by sampleChain attachment. */
  productSamples?: RawProductSample[];
  /** Core Product option lists (colourways/sizes/lengths) for this product, from the batch endpoint. */
  coreProductDetails?: CoreProductDetailsDto | undefined;
  /** Costing templates filtered for this product's dev order (same endpoint as product-costing-detail/add). */
  costingTemplates?: RawCostingTemplate[];
}

/** Derive a product's RowOptions from its already-resolved fetch results. */
function buildRowOptions(args: BuildRowOptionsArgs): RowOptions {
  const { productDetails, bomRes, otherRes, poDocRes, designRes, sampleRes, supplierId, poOptions, sampleTypeGroupRaw = [], productSamples } = args;
  const gradingGroups = (typeof productDetails === 'object' && productDetails?.gradingGroups) || [];
  const colourLibIds = typeof productDetails === 'object' && Array.isArray(productDetails?.colourLibraries) ? productDetails.colourLibraries : [];
  const colourwayOptions = colourLibIds
    .map((id) => args.colourLib.find((c) => c._id === id))
    .filter((c): c is { _id: string; name: string } => Boolean(c))
    .map((c) => opt(c.name, c._id));
  const bom = Array.isArray(bomRes) ? bomRes : [];
  const bomDocs = bom.filter((d) => !d.docType || d.docType === 'Quality').map((d) => opt(dated(d.fileName || '', d.createdAt), d._id));
  const colourDocs = bom.filter((d) => d.docType === 'Colour').map((d) => opt(dated(d.fileName || '', d.createdAt), d._id));
  const otherArr = Array.isArray(otherRes?.data) ? otherRes.data : [];
  const otherDocs = otherArr.map((d) => opt(d.documentTitle || '', d._id));
  const poDocs = (Array.isArray(poDocRes) ? poDocRes : []).map((d) => opt(d.fileName || '', d._id));
  const designArr = Array.isArray(designRes?.data) ? designRes.data : [];
  const designDocs = designArr.map((d) => opt(d.documentName || '', d._id));
  const sampleArr = Array.isArray(sampleRes?.data) ? sampleRes.data : [];
  const sampleOptions = sampleArr
    .filter((s) => s.supplier === supplierId)
    .map((s) => {
      const base = s.sampleNumber ? s.sampleNumber : '';
      const head = s.sampleTypeName ? `${base} - ${s.sampleTypeName} -` : base;
      return opt(dated(head, s.createdAt), s._id);
    });
  const samplesList = productSamples ?? (sampleArr as unknown as RawProductSample[]);
  /* Sample Refs come from the product's own samples, already loaded for chain attachment, so the
     dropdown costs no extra request. Deliberately not supplier-filtered: a costing's Sample Ref is
     scoped to the product, not to whichever supplier is selected at the top of the page. */
  const sampleRefOptions = samplesList.filter((sample) => sample?.sampleNumber).map((sample) => opt(sample.sampleRefLabel || (sample.sampleNumber as string), sample._id));
  /* In an AUTOMATED group Multi Submit only ever creates the first sample: once the product holds
     one, a further request attaches as the next version of it and never surfaces as a new row on the
     Samples page, so that group is raised from the Samples page instead. An unautomated group runs no
     progression, so it stays on offer however many samples the product already has in it. */
  const sampleTypeGroupOptions = sampleTypeGroupRaw
    .filter((g) => !productHasSampleInGroup(g, samplesList) || !isAutomatedSampleTypeGroup(g))
    .map((g) => opt(g.sampleGroupName, g._id));
  const legLengthOptions = typeof productDetails === 'object' && Array.isArray(productDetails?.legLengths) ? productDetails.legLengths : [];
  const costingTemplateOptions = (args.costingTemplates ?? []).filter((tmpl) => tmpl?.name).map((tmpl) => opt(tmpl.name as string, tmpl._id));
  return {
    poOptions,
    gradingGroups,
    sampleOptions,
    designDocs,
    colourDocs,
    bomDocs,
    otherDocs,
    poDocs,
    colourwayOptions,
    sampleRefOptions,
    sampleTypeGroupOptions,
    sampleTypeGroupRaw,
    samplesList,
    legLengthOptions,
    coreColourwayOptions: args.coreProductDetails?.colourways ?? [],
    coreSizeOptions: args.coreProductDetails?.sizes ?? [],
    coreLengthOptions: args.coreProductDetails?.lengths ?? [],
    costingTemplateOptions,
    /* Populated in MultiSubmit.tsx, where the page-level supplier factory master is available to
       resolve labels for this product's supplierFactories. */
    ciFactoryOptions: [],
  };
}

export interface MultiSubmitData {
  optionsByRow: Record<string, RowOptions>;
  trackingDates: TrackingDate[];
  userMentions: Array<{ id: string; display: string; label: string; name: string }>;
  loading: boolean;
}

/**
 * Lazily fetch option lists for the products on the current page (`pageDevOrderIds`).
 *
 * Supplier-scoped lookups (sample-tracking master, users, colour library) run once per supplier;
 * per-product fetches run only for ids not already loaded, so paging back to a visited page never
 * refetches. Results accumulate into `optionsByRow` keyed by id. Changing supplier drops every
 * per-supplier cache and reloads.
 */
export function useMultiSubmitData(pageDevOrderIds: string[], supplierId: string | null): MultiSubmitData {
  const dispatch = useDispatch<Dispatch>();
  const loggedInUserId = useSelector((state: { UserDetails?: { userInfo?: { userId?: string } } }) => state.UserDetails?.userInfo?.userId);
  const [optionsByRow, setOptionsByRow] = useState<Record<string, RowOptions>>({});
  const [trackingDates, setTrackingDates] = useState<TrackingDate[]>([]);
  const [userMentions, setUserMentions] = useState<MultiSubmitData['userMentions']>([]);
  const [loading, setLoading] = useState(false);

  const loadedIdsRef = useRef<Set<string>>(new Set());
  const colourLibRef = useRef<Array<{ _id: string; name: string }>>([]);
  const globalSupplierRef = useRef<string | null>(null);

  const idsKey = useMemo(() => pageDevOrderIds.join(','), [pageDevOrderIds]);

  useEffect(() => {
    if (pageDevOrderIds.length === 0 || !supplierId) return undefined;
    let active = true;

    setLoading(true);
    (async () => {
      try {
        if (globalSupplierRef.current !== supplierId) {
          loadedIdsRef.current = new Set();
          colourLibRef.current = [];
          if (active) setOptionsByRow({});

          const trackingRes = (await dispatch(getSampleTraking())) as RawTracking[] | undefined;
          const tracks: TrackingDate[] = (Array.isArray(trackingRes) ? trackingRes : []).filter((t) => t.isActive).map(mapTrackingEntry);
          const users = ((await dispatch(getCreatedByUser({ CompanyUsers: true, supplierId }))) as RawUser[]) || [];
          colourLibRef.current = ((await dispatch(fetchColorListLibrary({ loggedInUserId }))) as Array<{ _id: string; name: string }>) || [];

          if (!active) return;
          setTrackingDates(tracks);
          setUserMentions(users.map((u) => ({ id: u.email, display: `${u.email}, `, label: u.email, name: `${u.firstName} ${u.lastName}` })));
          globalSupplierRef.current = supplierId;
        }

        const idsToLoad = pageDevOrderIds.filter((id) => !loadedIdsRef.current.has(id));
        if (idsToLoad.length === 0) {
          if (active) setLoading(false);
          return;
        }

        const allPOs = ((await dispatch(getBulkFilteredPOs({ devOrderIds: idsToLoad, supplierIds: [supplierId] }))) as RawPO[]) || [];
        const posFor = (devOrderId: string): SelectOption[] =>
          allPOs
            .filter((po) => po.orderDetails?.some((d) => d.productNumber?.toString() === devOrderId.toString()))
            .map((po) => opt(po.purchaseOrderNumber || po.poOrderNo || `PO-${po._id}`, po._id));

        const [detailsByRow, coreByRow, bomByRow, otherByRow, poDocByRow, designByRow, samplesByRow, productSamplesByRow, sampleTypeGroupsByRow] =
          await Promise.all([
            getProductDetailsBatch(idsToLoad),
            getCoreProductDetailsBatch(idsToLoad),
            getBomDocumentsBatch(idsToLoad),
            getMeasurementDocumentsBatch(idsToLoad),
            getPoDocumentsBatch(idsToLoad),
            getDesignDocumentsBatch(idsToLoad),
            getSamplesBatch(idsToLoad, supplierId),
            getProductSamplesBatch(idsToLoad),
            getSampleTypeGroupsBatch(idsToLoad),
          ]);

        const templateEntries = await Promise.all(
          idsToLoad.map(async (devOrderId): Promise<[string, RawCostingTemplate[]]> => {
            const res = (await getCostingTemplateListByDevOrderService(devOrderId)) as { code?: number; data?: { doc?: RawCostingTemplate[] } };
            return [devOrderId, res?.code === 200 ? res?.data?.doc ?? [] : []];
          }),
        );
        const templatesByRow: Record<string, RawCostingTemplate[]> = Object.fromEntries(templateEntries);

        const entries = idsToLoad.map((devOrderId): [string, RowOptions] => {
          const rowOptions = buildRowOptions({
            productDetails: detailsByRow[devOrderId] ?? { gradingGroups: [], colourLibraries: [] },
            bomRes: bomByRow[devOrderId] ?? [],
            otherRes: { data: otherByRow[devOrderId] ?? [] },
            poDocRes: poDocByRow[devOrderId] ?? [],
            designRes: { data: designByRow[devOrderId] ?? [] },
            sampleRes: { data: samplesByRow[devOrderId] ?? [] },
            supplierId,
            poOptions: posFor(devOrderId),
            colourLib: colourLibRef.current,
            sampleTypeGroupRaw: (sampleTypeGroupsByRow[devOrderId] ?? []) as unknown as RawSampleTypeGroup[],
            productSamples: (productSamplesByRow[devOrderId] ?? []) as unknown as RawProductSample[],
            coreProductDetails: coreByRow[devOrderId],
            costingTemplates: templatesByRow[devOrderId] ?? [],
          });
          return [devOrderId, rowOptions];
        });

        if (!active) return;
        idsToLoad.forEach((id) => loadedIdsRef.current.add(id));
        setOptionsByRow((prev) => {
          const next = { ...prev };
          entries.forEach(([devOrderId, rowOptions]) => {
            next[devOrderId] = rowOptions;
          });
          return next;
        });
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [dispatch, idsKey, supplierId, loggedInUserId]);

  return { optionsByRow, trackingDates, userMentions, loading };
}

export { EMPTY_ROW_OPTIONS };
